import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/system";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { data_example } from "../../Pages/SeatingPlansPage/data_example";
import { useTranslation } from "react-i18next";
import { getRowLayout, ROW_NUMBER_INDEX } from "../../Helpers/seat_layout";
import { generateInvitationPDF } from "../../services/invitationPdfService";

// ---------------------- UPDATED PROPS ----------------------
interface SeatingCell {
  price: number | null;
  status: "open" | "invitation" | "closed" | "reservation"| "";
  ordered: "yes" | "no" | "";
  category: string;
  comment?: string;
  commenttDate?: string;
  previousCategory?: string; // track previous color/category
}

interface SeatingTableProps {
  seatingData: {
    [key: string]: {
      [key: string]: SeatingCell;
    };
  };
  onSeatingDataChange: (
    updatedSeatingData:
      | {
          [key: string]: {
            [key: string]: SeatingCell;
          };
        }
      | any
  ) => void;
  selectedHallHasSeatmap: boolean;

  // State from the parent for halls WITHOUT seatmaps
  countOfClosedSeats: string;
  countOfInvitationSeats: string;
  commentOfClosedSeats: string;
  commentOfInvitationSeats: string;
  setCountOfClosedSeats: React.Dispatch<React.SetStateAction<string>>;
  setCountOfInvitationSeats: React.Dispatch<React.SetStateAction<string>>;
  setCommentOfClosedSeats: React.Dispatch<React.SetStateAction<string>>;
  setCommentOfInvitationSeats: React.Dispatch<React.SetStateAction<string>>;

  // Event data for PDF generation
  eventName_en?: string;
  eventName_am?: string;
  eventDateTime?: string;
  eventCategory?: string;
  hallName_en?: string;
  hallName_am?: string;
  currentLanguage?: "en" | "am";
}
// ----------------------------------------------------------
const CustomCheckbox = styled(Checkbox)({
  "& .MuiSvgIcon-root": {
    fill: "transparent", // Make the checkbox background transparent
    stroke: "white", // Set the border color to white
  },
  "&.Mui-checked": {
    "& .MuiSvgIcon-root": {
      fill: "white", // Set the check color to white when checked
      stroke: "white", // Keep the border color white when checked
    },
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "0 5px",
  border: "none", // Ensure there are no borders
});

const StyledButton = styled(Button)({
  backgroundColor: "#3CAF2C",
  color: "white",
  borderRadius: "20px",
  margin: "10px",
  "&:hover": {
    backgroundColor: "#369f28",
  },
});

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    backgroundColor: "#f7f7f7",
    borderRadius: "10px",
    padding: "0px",
  },
});

const StyledDialogContent = styled(DialogContent)({
  padding: "20px 24px",
});

const StyledDialogActions = styled(DialogActions)({
  justifyContent: "flex-end",
  padding: "16px 24px",
});

const StyledDialogTitle = styled(DialogTitle)({
  fontWeight: "bold",
  color: "#333",
});

const StyledTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    color: "#333",
  },
  "& .MuiInput-underline:before": {
    borderBottomColor: "#ccc",
  },
  "& .MuiInput-underline:hover:before": {
    borderBottomColor: "#aaa",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#3CAF2C",
  },
});

/**
 * Helper to fill missing seat indexes in row 16
 */
function addMissingNumbers(arr: string[]): string[] {
  if (!arr.length) return [];
  const numArr: number[] = arr.map(Number);
  const min: number = Math.min(...numArr);
  const max: number = Math.max(...numArr);
  const currentNumbers: Set<number> = new Set(numArr);
  const completeRange: number[] = Array.from(
    { length: max - min + 1 },
    (_, i) => i + min
  );
  const missingNumbers: number[] = completeRange.filter(
    (num) => !currentNumbers.has(num)
  );
  const missingNumbersAsString: string[] = missingNumbers.map(String);
  return [...arr, ...missingNumbersAsString].sort(
    (a, b) => Number(a) - Number(b)
  );
}

const EventSeatingPlanTable: React.FC<SeatingTableProps> = ({
  seatingData,
  onSeatingDataChange,
  selectedHallHasSeatmap,

  // State & setters from the parent for halls WITHOUT seatmaps
  countOfClosedSeats,
  countOfInvitationSeats,
  commentOfClosedSeats,
  commentOfInvitationSeats,
  setCountOfClosedSeats,
  setCountOfInvitationSeats,
  setCommentOfClosedSeats,
  setCommentOfInvitationSeats,

  // Event data for PDF generation
  eventName_en,
  eventName_am,
  eventDateTime,
  eventCategory,
  hallName_en,
  hallName_am,
  currentLanguage = "en",
}) => {
  const { t } = useTranslation();
  // For seatmap: track which seats are selected
  const [selectedCells, setSelectedCells] = useState<{
    [key: string]: { [key: string]: SeatingCell };
  }>({});

  // For seatmap & generic usage: show/hide modal, track action type
  const [openModal, setOpenModal] = useState(false);
  const [actionType, setActionType] = useState<"" | "invitation" | "close">("");

  // For seatmap only: we store a local comment for each seat selection
  // (For seatmap = false, we will use the parent's comment states.)
  const [comment, setComment] = useState("");

  // Generate 15 rows instead of using existing data keys
  const rowsIndexArr = Array.from({ length: 15 }, (_, i) => (i + 1).toString());

  /**
   * For seatmap: handle seat click
   */
  const handleCellClick = (rowIndex: string, colIndex: string) => {
    const tempObj = { ...selectedCells };

    if (!tempObj[rowIndex]) {
      tempObj[rowIndex] = {};
    }

    if (tempObj[rowIndex][colIndex]) {
      // If already selected, unselect it
      delete tempObj[rowIndex][colIndex];
      if (Object.keys(tempObj[rowIndex]).length === 0) {
        delete tempObj[rowIndex];
      }
    } else {
      // Else select it
      // fallback if seatingData[rowIndex]?.[colIndex] is undefined
      const cellData =
        seatingData[rowIndex]?.[colIndex] || {
          price: null,
          status: "open",
          ordered: "",
          category: "",
        };
      tempObj[rowIndex][colIndex] = { ...cellData, category: "aqua" };
    }

    setSelectedCells(tempObj);
  };

  /**
   * For seatmap: handle row-level checkbox
   */
  const handleRowCheckboxChange = (rowIndex: string) => {
    setSelectedCells((prevSelectedCells) => {
      const newSelectedCells = { ...prevSelectedCells };
      // Instead of data_example, we want the actual seat definition from `seatingData`:
      const currentColumns =
        seatingData[rowIndex] ||
        {}; // fallback to empty object to avoid runtime error
      const totalSeatsInRow = Object.keys(currentColumns).length;
      const alreadySelectedCount = newSelectedCells[rowIndex]
        ? Object.keys(newSelectedCells[rowIndex]).length
        : 0;

      // If row is fully selected, unselect the entire row
      if (alreadySelectedCount === totalSeatsInRow) {
        delete newSelectedCells[rowIndex];
      } else {
        // Select entire row
        newSelectedCells[rowIndex] = {};
        Object.keys(currentColumns).forEach((colIndex) => {
          // fallback if seat is missing
          const seatCell = seatingData[rowIndex]?.[colIndex] || {
            price: null,
            status: "open",
            ordered: "no",
            category: "white",
          };
          newSelectedCells[rowIndex][colIndex] = {
            ...seatCell,
            category: "aqua", // highlight selected
          };
        });
      }

      return newSelectedCells;
    });
  };

  /**
   * Button "Select for Invitation" => open modal
   */
  const handleSelectForInvitation = () => {
    setActionType("invitation");
    setOpenModal(true);
  };

  /**
   * Button "Close Seats" => open modal
   */
  const handleCloseSeats = () => {
    setActionType("close");
    setOpenModal(true);
  };

  /**
   * Submit (inside modal)
   */
  const handleModalSubmit = async () => {
    // 1) If the hall HAS a seatmap => we apply changes to each selected seat
    if (selectedHallHasSeatmap) {
      if (actionType === "invitation") {
        const updatedSeatingData = { ...seatingData };
        const invitationSeats: any[] = [];
        
        for (const rowIndex in selectedCells) {
          for (const colIndex in selectedCells[rowIndex]) {
            updatedSeatingData[rowIndex][colIndex] = {
              ...updatedSeatingData[rowIndex][colIndex],
              status: "invitation",
              ordered: "yes",
              category: "red",
              comment: comment || "",
              commenttDate: new Date().toISOString(),
            };
            
            // Collect seat information for PDF generation
            invitationSeats.push({
              row: rowIndex,
              seat: colIndex,
            });
          }
        }
        
        // Generate invitation PDFs if event data is available
        if (eventName_en && eventName_am && eventDateTime && hallName_en && hallName_am) {
          try {
            const eventName = currentLanguage === "am" ? eventName_am : eventName_en;
            const hallName = currentLanguage === "am" ? hallName_am : hallName_en;
            
            const invitationData = {
              eventName,
              eventDate: eventDateTime,
              hallName,
              language: currentLanguage,
              translations: {
                invitation: t("Invitation"),
                youAreInvited: t("You are cordially invited to"),
                eventDate: t("Event Date"),
                eventName: t("Event Name"),
                venue: t("Venue"),
                seat: t("Seat"),
                row: t("Row"),
                accessText: t("This invitation grants you access to the event"),
                presentText: t("Please present this invitation at the entrance"),
              }
            };

            await generateInvitationPDF(invitationData, invitationSeats);
            toast.success(`Selected seats are now marked for invitation! PDF generated with ${invitationSeats.length} invitation(s).`);
          } catch (error) {
            console.error("Error generating invitation PDF:", error);
            toast.success("Selected seats are now marked for invitation!");
            toast.warn("PDF generation failed, but seats were marked successfully.");
          }
        } else {
          toast.success("Selected seats are now marked for invitation!");
          toast.info("PDF generation requires complete event information.");
        }
        
        setSelectedCells({});
        onSeatingDataChange(updatedSeatingData);
      } else if (actionType === "close") {
        const updatedSeatingData = { ...seatingData };
        for (const rowIndex in selectedCells) {
          for (const colIndex in selectedCells[rowIndex]) {
            const previousCategory =
              updatedSeatingData[rowIndex][colIndex].category;
            updatedSeatingData[rowIndex][colIndex] = {
              ...updatedSeatingData[rowIndex][colIndex],
              previousCategory, // track the old color before closing
              status: "closed",
              category: "#D3D3D3",
              comment: comment || "",
              commenttDate: new Date().toISOString(),
            };
          }
        }
        setSelectedCells({});
        onSeatingDataChange(updatedSeatingData);
        toast.info("Selected seats are now closed!");
      }

      // 2) If the hall does NOT have a seatmap => update invitation/closed counts & comments from parent-state
    } else {
      const updatedSeatingData: any = {
        ...seatingData,
        comment:
          actionType === "invitation"
            ? commentOfInvitationSeats
            : commentOfClosedSeats,
        commenttDate: new Date().toISOString(),
      };

      if (actionType === "invitation") {
        updatedSeatingData["_invitationCount"] = countOfInvitationSeats || "0";
        
        // Generate general invitation PDF for halls without seatmap
        if (eventName_en && eventName_am && eventDateTime && hallName_en && hallName_am) {
          try {
            const eventName = currentLanguage === "am" ? eventName_am : eventName_en;
            const hallName = currentLanguage === "am" ? hallName_am : hallName_en;
            const invitationCount = parseInt(countOfInvitationSeats || "0");
            
            if (invitationCount > 0) {
              const invitationData = {
                eventName,
                eventDate: eventDateTime,
                hallName,
                language: currentLanguage,
                translations: {
                  invitation: t("Invitation"),
                  youAreInvited: t("You are cordially invited to"),
                  eventDate: t("Event Date"),
                  eventName: t("Event Name"),
                  venue: t("Venue"),
                  seat: t("Seat"),
                  row: t("Row"),
                  accessText: t("This invitation grants you access to the event"),
                  presentText: t("Please present this invitation at the entrance"),
                }
              };
              
              // Generate general invitations (no specific seats)
              const generalInvitations = Array.from({ length: invitationCount }, (_, index) => null);
              await generateInvitationPDF(invitationData, generalInvitations);
              
              toast.success(`Invitations set: ${countOfInvitationSeats || "0"} - PDF generated!`);
            } else {
              toast.success(`Invitations set: ${countOfInvitationSeats || "0"} (Comment added if any.)`);
            }
          } catch (error) {
            console.error("Error generating invitation PDF:", error);
            toast.success(`Invitations set: ${countOfInvitationSeats || "0"} (Comment added if any.)`);
            toast.warn("PDF generation failed, but invitations were set successfully.");
          }
        } else {
          toast.success(`Invitations set: ${countOfInvitationSeats || "0"} (Comment added if any.)`);
        }
      } else if (actionType === "close") {
        updatedSeatingData["_closedCount"] = countOfClosedSeats || "0";
        toast.info(
          `Closed seats set: ${countOfClosedSeats || "0"} (Comment added if any.)`
        );
      }

      onSeatingDataChange(updatedSeatingData);
    }

    // Close the modal, reset local states
    setOpenModal(false);
    setComment("");
    setActionType("");
  };

  /**
   * Close the modal without changes
   */
  const handleModalClose = () => {
    setOpenModal(false);
    setComment("");
    setActionType("");
  };

  /**
   * RENDER
   */
  console.log( seatingData)
  return (
    <div
      style={{
        padding: "10px",
        width: "80%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <ToastContainer />
      <div style={{ marginBottom: "20px" }}>
        <StyledButton
          variant="contained"
          onClick={handleSelectForInvitation}
          style={{
            marginRight: "10px",
            backgroundColor: "#3CAF2C",
            borderRadius: "40px",
          }}
        >
          {t("Select for Invitation")}
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleCloseSeats}
          style={{
            marginRight: "10px",
            backgroundColor: "#3CAF2C",
            borderRadius: "40px",
          }}
        >
          {t("Close Seats")}
        </StyledButton>
      </div>

      {/**
       * If the hall HAS a seatmap => Show seatmap table
       * Otherwise, we skip rendering the table entirely
       */}
      {selectedHallHasSeatmap && (
        <Paper
          style={{
            width: "70%",
            maxWidth: "800px",
            backgroundColor: "#17392b",
            padding: "30px",
            borderRadius: "10px",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "30px",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              marginLeft: "60px",
            }}
          >


<svg width="604" height="62" viewBox="0 0 604 62" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M598 6C482.821 68.024 144 77.1532 6 6.00008" stroke="white" stroke-width="12" stroke-linecap="round"/>
</svg>

            <Box
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "white",
                position: "absolute",
                bottom: "45px",
              }}
            >
              Stage
            </Box>
          </Box>

          <Table size="small">
          <TableBody>
  {rowsIndexArr.map((row_index) => {
    const currentColumns = seatingData?.[row_index] || {};
    const seatCount = Object.keys(currentColumns).length;
    const selectedCount = Object.keys(selectedCells[row_index] || {}).length;

    return (
      <React.Fragment key={`row-${row_index}`}>
        <TableRow style={{ height: "30px" }}>
          <StyledTableCell padding="checkbox">
            <CustomCheckbox
              size="small"
              checked={seatCount > 0 && selectedCount === seatCount}
              onChange={() => handleRowCheckboxChange(row_index)}
            />
          </StyledTableCell>

          {/* Seats laid out from the seatmap JSON (see getRowLayout), row number in the middle */}
          {getRowLayout(currentColumns).map((column_index, index) => {
            const cellData = column_index !== null ? currentColumns[column_index] : undefined;
            const price = cellData?.price || "N/A";
            const isClosed = cellData?.status === "closed" || cellData?.status === "reservation";

            return (
              <React.Fragment key={`row-${row_index}-pos-${index}`}>
                {index === ROW_NUMBER_INDEX && (
                  <TableCell
                    style={{
                      backgroundColor: "transparent",
                      padding: 0,
                      textAlign: "center",
                      border: "none",
                      width: "20px",
                      position: "relative",
                      color: "#ffffff",
                    }}
                  >
                    {row_index}
                  </TableCell>
                )}
                {column_index === null || !cellData ? (
                  // No seat at this position in the seatmap
                  <TableCell
                    style={{
                      backgroundColor: "transparent",
                      padding: 0,
                      border: "none",
                      width: "20px",
                    }}
                  />
                ) : (
                  <TableCell
                    onClick={!isClosed ? () => handleCellClick(row_index, column_index) : undefined}
                    style={{
                      padding: 0,
                      textAlign: "center",
                      border: "none",
                      cursor: isClosed ? "not-allowed" : "pointer",
                      width: "20px",
                      position: "relative",
                      color: "#ffffff",
                    }}
                  >
                    <Tooltip title={isClosed ? "Closed Seat" : `Price: ֏ ${price} + ${row_index} + ${column_index}`}>
                      <div
                        style={{
                          backgroundColor: selectedCells[row_index]?.[column_index]?.category === "aqua"
                            ? "aqua"
                            : isClosed
                            ? "#D3D3D3"  // Gray for closed/purchased seats
                            : cellData?.category || "white",
                          textAlign: "center",
                          fontSize: "8px",
                          margin: "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "20px",
                          height: "20px",
                          borderTopLeftRadius: "10px",
                          borderTopRightRadius: "10px",
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                )}
              </React.Fragment>
            );
          })}
        </TableRow>

        {/* Empty spacing row after row 7 */}
        {row_index === "7" && (
          <TableRow style={{ height: "30px" }}>
            {Array.from({ length: 19 }, (_, index) => (
              <TableCell
                key={`empty-col-${index}`}
                style={{
                  backgroundColor: "transparent",
                  padding: 0,
                  border: "none",
                  width: "20px",
                }}
              />
            ))}
          </TableRow>
        )}
      </React.Fragment>
    );
  })}
</TableBody>

          </Table>
        </Paper>
      )}

      {/**
       * Modal for:
       *  - "Select for Invitation"
       *  - "Close Seats"
       */}
      <StyledDialog
        open={openModal}
        onClose={handleModalClose}
        PaperProps={{
          sx: {
            width: "600px",
            minHeight: "200px",
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: 3,
          },
        }}
      >
        <StyledDialogTitle
          sx={{
            backgroundColor: "#4D67EB",
            color: "#fff",
            fontSize: "1.2rem",
            fontWeight: "bold",
            textAlign: "center",
            py: 2,
          }}
        >
          {actionType === "invitation" ? t("Select for Invitation") : t("Close Seats")}
        </StyledDialogTitle>

        <StyledDialogContent
          sx={{
            backgroundColor: "#4D67EB",
            px: 3,
            py: 3,
          }}
        >
          {/**
           * Only show numeric input if the hall does NOT have a seatmap
           */}
          {!selectedHallHasSeatmap && (
            <StyledTextField
              autoFocus
              type="number"
              placeholder={
                actionType === "invitation"
                  ? "Count Of Invitations"
                  : "Count Of Closed seats"
              }
              fullWidth
              variant="filled"
              InputProps={{
                disableUnderline: true,
                sx: {
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  fontSize: "1rem",
                },
              }}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "#fff",
                  mb: 2,
                },
                "& .MuiFilledInput-root:hover": {
                  backgroundColor: "#fff !important",
                },
                "& .MuiFilledInput-root.Mui-focused": {
                  backgroundColor: "#fff !important",
                },
              }}
              value={
                actionType === "invitation"
                  ? countOfInvitationSeats
                  : countOfClosedSeats
              }
              onChange={(e) => {
                if (actionType === "invitation") {
                  setCountOfInvitationSeats(e.target.value);
                } else {
                  setCountOfClosedSeats(e.target.value);
                }
              }}
            />
          )}

          {/**
           * For seatmap = false => read/write parent's comment states
           * For seatmap = true  => local comment state
           */}
          <StyledTextField
            placeholder="Enter your comment..."
            fullWidth
            multiline
            rows={5}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              sx: {
                backgroundColor: "#fff",
                borderRadius: "5px",
                fontSize: "1rem",
                "&::placeholder": {
                  color: "#888",
                  opacity: 1,
                },
              },
            }}
            sx={{
              "& .MuiFilledInput-root": {
                backgroundColor: "#fff",
              },
              "& .MuiFilledInput-root:hover": {
                backgroundColor: "#fff !important",
              },
              "& .MuiFilledInput-root.Mui-focused": {
                backgroundColor: "#fff !important",
              },
            }}
            value={
              selectedHallHasSeatmap
                ? comment // local state
                : actionType === "invitation"
                ? commentOfInvitationSeats
                : commentOfClosedSeats
            }
            onChange={(e) => {
              if (selectedHallHasSeatmap) {
                // For seatmap => local
                setComment(e.target.value);
              } else {
                // For no seatmap => parent's states
                if (actionType === "invitation") {
                  setCommentOfInvitationSeats(e.target.value);
                } else {
                  setCommentOfClosedSeats(e.target.value);
                }
              }
            }}
          />
        </StyledDialogContent>

        <StyledDialogActions
          sx={{
            backgroundColor: "#4D67EB",
            pb: 3,
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleModalClose}
            style={{
              color: "#4D67EB",
              backgroundColor: "#fff",
              textTransform: "none",
              borderRadius: "5px",
              padding: "8px 16px",
              marginRight: "8px",
            }}
          >
            Cancel
          </Button>

          <StyledButton
            onClick={handleModalSubmit}
            style={{
              color: "#fff",
              backgroundColor: "rgb(60, 175, 44)",
              textTransform: "none",
              borderRadius: "5px",
              padding: "8px 16px",
            }}
          >
            Submit
          </StyledButton>
        </StyledDialogActions>
      </StyledDialog>
    </div>
  );
};

export default EventSeatingPlanTable;